<template>
  <div class="contract-settings">
    <div v-if="origin === 'single_tool'" class="alert alert-info">
      Pour obtenir un taux net sécurisé, nous vous recommandons d'utiliser dans la simulation le maximum de jours
      travaillés possibles dans un mois. Cela permet de vérifier que le taux net reste sous le plafond de {{ toMoney(maxNetHelp) }} net,
      même lors des mois les plus chargés. Par exemple pour 5j d'accueil par semaine noter 22 jours.
    </div>

    <RadioGroup
      v-if="origin === 'single_tool'"
      :model-value="monthWeek"
      label="Saisie des infos"
      :options="monthWeekOptions"
      name="month_week"
      @update:model-value="$emit('update:monthWeek', $event)"
    />

    <div v-if="isMonth" class="overtime-container">
      <ToggleInput
        :model-value="hasOvertime"
        label="Heures majorées&nbsp;?"
        input-class="planned_overtime"
        @update:model-value="$emit('update:hasOvertime', $event)"
      />
    </div>

    <div v-if="monthWeek !== 'other'" class="month-datas">
      <!-- Monthly inputs -->
      <div v-if="isMonth">
        <DecimalInput
          :model-value="monthlyWorkedDays"
          label="Jours d'accueil mensualisés"
          append="jours"
          :precision="4"
          @update:model-value="$emit('update:monthlyWorkedDays', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le nombre mensualisé de jours pendant lesquels votre enfant est confié à votre assistant(e) maternel(le).</p>'" />
          </template>
          <template #error>
            <div v-if="!isMonthlyWorkedDaysValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
      </div>

      <div v-if="isMonth">
        <DecimalInput
          :model-value="monthlyHours"
          label="Heures d'accueil mensualisées"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:monthlyHours', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="`<p>Le volume horaire <b>mensualisé</b> prévu au contrat.</p><p>Ce chiffre est obtenu en effectuant la mensualisation de vos différents plannings d'accueil.</p>`" />
          </template>
          <template #error>
            <div v-if="!isMonthlyHoursValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
      </div>

      <!-- Weekly inputs -->
      <div v-if="isWeek">
        <DecimalInput
          :model-value="weeklyWorkedDays"
          label="Jours par semaine"
          append="jours"
          :precision="2"
          @update:model-value="$emit('update:weeklyWorkedDays', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le nombre moyen de jours par semaine pendant lesquels votre enfant est confié à votre assistant(e) maternel(le).</p>'" />
          </template>
          <template #error>
            <div v-if="!isWeeklyWorkedDaysValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
      </div>

      <div v-if="isWeek">
        <DecimalInput
          :model-value="weeklyHours"
          label="Heures par semaine"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:weeklyHours', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le volume horaire hebdomadaire moyen prévu au contrat.</p>'" />
          </template>
          <template #error>
            <div v-if="!isWeeklyHoursValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>

        <div v-if="weeklyHoursFloat > 48" class="alert alert-danger mt-2">
          Attention, le volume horaire saisi dépasse le maximum autorisé de 48 heures par semaine.
        </div>
        <div v-else-if="weeklyHoursFloat > 45" class="alert alert-warning mt-2">
          Attention, le volume horaire saisi dépasse 45 heures par semaine.
        </div>
      </div>

      <!-- Yearly weeks -->
      <div>
        <DecimalInput
          :model-value="yearlyWeeks"
          label="Semaines d'accueil / an (52 ou <47)"
          append="semaines"
          :precision="0"
          @update:model-value="$emit('update:yearlyWeeks', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le nombre de semaines par an prévues au contrat.</p>'" />
          </template>
          <template #error>
            <div v-if="!isYearlyWeeksValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
        <div
          v-if="contractEndsBeforeOneYear && schedulableWeeks && weeksWorked"
          class="label form-text mb-3"
          style="margin-top: -10px"
        >
          Contrat sur <b>{{ schedulableWeeks }} semaines</b>
          (dont <b>{{ weeksWorked }} d'accueil</b>).
          Équivalent annuel pour la simulation : <b>{{ yearlyWeeksFloat }} semaines/an</b>.
        </div>
        <div v-if="contractEndsBeforeOneYear && yearlyWeeksFloat > 46 && yearlyWeeksFloat < 52" class="label">
          Dans le cas d'un contrat <b>planifié sur moins d'un an</b>, le nombre de semaine travaillées est ramené
          à l'année entière, et peut donc dépasser 46 !
        </div>
      </div>

      <!-- Monthly overtime (when month mode + has overtime) -->
      <div v-if="isMonth && hasOvertime">
        <DecimalInput
          :model-value="monthlyOvertime"
          label="Combien d'heures majorées par mois ?"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:monthlyOvertime', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="`<p>Indiquez ici le volume horaire <b>mensualisé</b> des heures majorées prévues au contrat.</p><p>Ce chiffre est obtenu en effectuant la mensualisation des heures majorées de vos différents plannings d'accueil.</p>`" />
          </template>
          <template #error>
            <div v-if="!isMonthlyOvertimeValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>

        <SelectInput
          :model-value="increaseOvertime"
          label="Avec une majoration du salaire de :"
          :options="overtimeOptions"
          @update:model-value="$emit('update:increaseOvertime', $event)"
        />
      </div>

      <!-- Weekly overtime display -->
      <div v-if="isWeek && weeklyHoursFloat > 45" class="weekly_overtime">
        <div class="row mb-3">
          <label class="col-sm-6 col-form-label bold">Heures maj. par semaine :</label>
          <div class="col-sm row mx-0 align-items-center">
            <div class="col form-control bold" readonly>{{ toDecimal4(weeklyHoursFloat - 45) }}</div>
            <div class="col-2 form-text p-0 text-right">
              <Popup :icon="iconQuestion" :body="'<p>Les heures travaillées au-delà de 45 heures par semaine. On les appelle communément les <b>heures majorées</b>. Elles sont obligatoirement majorées.</p><p>Ce taux est à négocier entre les parties. Généralement il est de 25%.</p>'" />
            </div>
          </div>
        </div>

        <SelectInput
          :model-value="increaseOvertime"
          label="Avec une majoration du salaire de :"
          :options="overtimeOptions"
          @update:model-value="$emit('update:increaseOvertime', $event)"
        />
      </div>
    </div>

    <!-- Allowances -->
    <div :style="monthWeek === 'other' ? 'display: none;' : 'display:block;'">
      <DecimalInput
        :model-value="ie"
        label="Indemnités entretien"
        append="€ par jour"
        :precision="2"
        @update:model-value="$emit('update:ie', $event)"
      >
        <template #hint>
          <Popup :icon="iconQuestion" :body="`<p>Les indemnités d'entretien couvrent les frais engagés par l'assistant(e) maternel(le) pour l'accueil de votre enfant (matériel, produits d'hygiène, part d'énergie, etc.).</p>`" />
        </template>
        <template #error>
          <div v-if="!isMaintenanceAllowanceValid" class="invalid-feedback error">Valeur incorrecte</div>
        </template>
      </DecimalInput>

      <DecimalInput
        :model-value="mealDailyAllowance"
        label="Indemnités repas"
        append="€ par jour"
        :precision="2"
        @update:model-value="$emit('update:mealDailyAllowance', $event)"
      >
        <template #hint>
          <Popup :icon="iconQuestion" :body="mealTooltipHtml" />
        </template>
      </DecimalInput>

      <!-- Rémunération : slider + manual-input stepper -->
      <div id="hourly-rate-container" class="cmg-remuneration">
        <h6 class="cmg-remuneration__title mb-3">Rémunération</h6>

        <RangeSlider
          v-if="minimumGrossHourlyWage > 0"
          :model-value="grossHourlyWageFloat"
          :min="minimumGrossHourlyWage"
          :max="maximumGrossHourlyWage"
          :step="0.01"
          :format-tooltip="formatWageTooltip"
          @update:model-value="onSliderInput"
          @slider-ready="$emit('slider-ready', $event)"
        />

        <div class="cmg-remuneration__manual mt-4">
          <div class="text-center font--bold mb-1">
            Saisie manuelle du taux horaire brut
          </div>
          <div class="d-flex align-items-center justify-content-center gap-3">
            <button type="button" class="cmg-hourly-rate-btn" @click="$emit('adjustHourlyRate', 'decrease')" v-html="icons.iconSignMinus" />
            <div class="input-group" style="width: auto !important;">
              <input
                type="text"
                class="form-control"
                :value="grossHourlyWageInput"
                data-precision="2"
                @input="$event.target.value = $event.target.value.replace('.', ','); $emit('update:grossHourlyWageInput', $event.target.value); $emit('updateHourlyWage')"
              />
              <span class="input-group-text">€ (brut)</span>
            </div>
            <button type="button" class="cmg-hourly-rate-btn" @click="$emit('adjustHourlyRate', 'increase')" v-html="icons.iconSignPlus" />
          </div>
        </div>

        <div v-if="!isHourlyRateInRange" class="text-danger font--xs mt-2">
          La plage de valeurs recommandées est comprise entre {{ toFloatForDisplay(minimumGrossHourlyWage, 2) }} €
          et {{ maximumGrossHourlyWage }} €.
        </div>
        <div v-if="maxCMGHelpExceeded" class="alert alert-danger mt-4 font--xs">
          Avec la nouvelle reforme de Septembre 2025, le plafond d'aide est à {{ toMoney(maxNetHelp) }} / heure (net),
          incluant les indemnités d'entretien et repas.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import {
  DecimalInput,
  Popup,
  RadioGroup,
  RangeSlider,
  SelectInput,
  ToggleInput,
  iconQuestion,
} from '@twg/ui';
import { iconSignMinus, iconSignPlus } from '../icons';

export default defineComponent({
  name: 'ContractSettings',
  components: { DecimalInput, Popup, RadioGroup, RangeSlider, SelectInput, ToggleInput },
  props: {
    monthWeek: { type: String, required: true },
    weeklyWorkedDays: { type: String, required: true },
    weeklyHours: { type: String, required: true },
    yearlyWeeks: { type: String, required: true },
    monthlyWorkedDays: { type: String, required: true },
    monthlyHours: { type: String, required: true },
    hasOvertime: { type: Boolean, required: true },
    monthlyOvertime: { type: String, required: true },
    increaseOvertime: { type: String, required: true },
    contractEndsBeforeOneYear: { type: Boolean, default: false },
    schedulableWeeks: { type: Number, default: null },
    weeksWorked: { type: Number, default: null },
    ie: { type: String, required: true },
    mealDailyAllowance: { type: String, required: true },
    grossHourlyWageInput: { type: String, required: true },
    grossHourlyWageFloat: { type: Number, required: true },
    netRatioClassic: { type: Number, required: true },
    isHourlyRateInRange: { type: Boolean, default: true },
    minimumGrossHourlyWage: { type: Number, default: 0 },
    maximumGrossHourlyWage: { type: Number, default: 15 },
    maxCMGHelpExceeded: { type: Boolean, default: false },
    maxNetHelp: { type: Number, required: true },
    // Validation props
    isMonthlyWorkedDaysValid: { type: Boolean, default: true },
    isMonthlyHoursValid: { type: Boolean, default: true },
    isWeeklyWorkedDaysValid: { type: Boolean, default: true },
    isWeeklyHoursValid: { type: Boolean, default: true },
    isYearlyWeeksValid: { type: Boolean, default: true },
    isMonthlyOvertimeValid: { type: Boolean, default: true },
    isMaintenanceAllowanceValid: { type: Boolean, default: true },
    origin: { type: String, default: 'single_tool' },
    toFloatForDisplay: { type: Function, required: true },
    toMoney: { type: Function, required: true },
    toDecimal4: { type: Function, required: true },
  },
  emits: [
    'update:monthWeek', 'update:weeklyWorkedDays', 'update:weeklyHours',
    'update:yearlyWeeks', 'update:monthlyWorkedDays', 'update:monthlyHours',
    'update:hasOvertime', 'update:monthlyOvertime', 'update:increaseOvertime',
    'update:ie', 'update:mealDailyAllowance', 'update:grossHourlyWageInput',
    'adjustHourlyRate', 'updateHourlyWage', 'slider-ready',
    'setGrossHourlyWageFromSlider',
  ],
  setup(props, ctx) {
    const isMonth = computed(() => props.monthWeek === 'month');
    const isWeek = computed(() => props.monthWeek === 'week');

    const weeklyHoursFloat = computed(() => parseFloat(props.weeklyHours.replace(',', '.')));
    const yearlyWeeksFloat = computed(() => parseFloat(props.yearlyWeeks.replace(',', '.')));

    const monthWeekOptions = computed(() => {
      const options = [
        { label: 'au mois', value: 'month' },
        { label: 'à la semaine', value: 'week' },
      ];
      if (props.origin !== 'contract_editor') {
        options.push({ label: 'autre', value: 'other' });
      }
      return options;
    });

    const overtimeOptions = Array.from({ length: 9 }, (_, i) => ({
      label: `${(i + 2) * 5} %`,
      value: String((i + 2) * 5),
    }));

    const icons = { iconSignMinus, iconSignPlus };

    const mealTooltipHtml = [
      "<p>L'indemnité repas journalière est la moyenne pondérée des repas fournis sur l'ensemble des plannings du contrat.</p>",
      '<p>Le calcul prend en compte :</p>',
      '<ul>',
      "<li>le nombre de jours d'accueil avec repas par planning</li>",
      '<li>le nombre de semaines de chaque planning</li>',
      '<li>les différents montants des repas (petit-déjeuner, déjeuner, goûter, dîner)</li>',
      '</ul>',
      '<table class="table table-bordered text-center">',
      '<thead><tr><th>Étape</th><th>Calcul</th></tr></thead>',
      '<tbody>',
      '<tr><td>Moyenne par jour</td><td>Somme des repas du planning / nombre de jours avec repas</td></tr>',
      '<tr><td>Total annuel</td><td>Moyenne par jour × jours × semaines du planning</td></tr>',
      '<tr><td>Résultat</td><td>Total annuel tous plannings / total jours travaillés annuels</td></tr>',
      '</tbody></table>',
    ].join('');

    // Tooltip on the rémunération slider — bold gross + small "(X net)" line.
    // Driven by netRatioClassic so the net figure updates live when the
    // region changes.
    function formatWageTooltip(value) {
      const grossLabel = props.toMoney(value);
      const netLabel = props.toMoney(value * props.netRatioClassic);
      return `<b>${grossLabel}</b><br /><span class="font--xs">(${netLabel} net)</span>`;
    }

    // Slider drag → write directly via setGrossHourlyWageFromSlider.
    // We deliberately bypass updateHourlyWage: it calls slider.set() to
    // mirror the manual input back onto the slider, which during a drag
    // creates a feedback loop (slider → state → slider.set → noUiSlider
    // `update` → state again) and is the dominant cause of drag lag.
    function onSliderInput(numericValue) {
      ctx.emit('setGrossHourlyWageFromSlider', numericValue);
    }

    return {
      isMonth,
      isWeek,
      weeklyHoursFloat,
      yearlyWeeksFloat,
      monthWeekOptions,
      overtimeOptions,
      icons,
      iconQuestion,
      mealTooltipHtml,
      formatWageTooltip,
      onSliderInput,
    };
  },
});
</script>
